import React, { useMemo, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft, ExternalLink, Copy, Check, Search,
} from "lucide-react";
import AdminAuthGate from "@/components/admin/AdminAuthGate";
import ROUTES, { CATEGORIES, buildPreviewPath, paramNames } from "@/lib/routesRegistry";

// ── AdminRoutesPage ──────────────────────────────────────────────────────
//
// Live directory of every URL registered in the app. Reads from the
// same `routesRegistry.jsx` module that `App.js` mounts, so adding or
// removing a route in the registry updates this page automatically —
// no manual maintenance required.
//
// Each row shows:
//   • path pattern (with :params highlighted)
//   • page name + short description
//   • category badge (public / editorial / admin)
//   • parameter list (if any)
//   • "Open" button — new tab, params filled with sample values so the
//     link lands on a real rendered page
//   • "Copy" button — copies the resolved URL to clipboard
//
// Sidebar filters by category + a search box that matches path, name
// and description, mirroring the UX of /admin/images so admins pick
// it up instantly.

const CATEGORY_BADGE_STYLES = {
  public:    "bg-[#3F6E4A] text-[#FDFBF7]",
  editorial: "bg-[#C16542] text-[#FDFBF7]",
  admin:     "bg-[#2C2621] text-[#FDFBF7]",
};

// Fallback badge if a new category shows up before its style is
// registered — keeps rendering deterministic while still readable.
const DEFAULT_BADGE_STYLE = "bg-[#E8DBC4] text-[#5C5248]";

const AdminRoutesPageInner = () => {
  const [q, setQ]           = useState("");
  const [activeCat, setCat] = useState("all");
  const [copiedPath, setCopiedPath] = useState(null);

  // Origin of the deployed app — the preview + production domain the
  // "Open" and "Copy" buttons should target. Falls back to the current
  // location when the env var isn't defined (should never happen in
  // this app, but defensive against local dev).
  const origin = (typeof window !== "undefined" && window.location)
    ? window.location.origin
    : "";

  const stats = useMemo(() => {
    const out = { total: ROUTES.length };
    ROUTES.forEach((r) => { out[r.category] = (out[r.category] || 0) + 1; });
    return out;
  }, []);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return ROUTES.filter((r) => {
      if (activeCat !== "all" && r.category !== activeCat) return false;
      if (!needle) return true;
      const hay = `${r.path} ${r.name} ${r.description || ""}`.toLowerCase();
      return hay.includes(needle);
    });
  }, [q, activeCat]);

  // Group filtered results by category so each section is scannable —
  // the ordering in the registry is preserved within each group.
  const grouped = useMemo(() => {
    const groups = {};
    filtered.forEach((r) => {
      (groups[r.category] = groups[r.category] || []).push(r);
    });
    // Emit ordered array to preserve CATEGORIES order.
    return Object.keys(CATEGORIES)
      .filter((k) => groups[k]?.length > 0)
      .map((k) => ({ key: k, routes: groups[k] }));
  }, [filtered]);

  const handleCopy = useCallback(async (entry) => {
    const url = origin + buildPreviewPath(entry);
    try {
      await navigator.clipboard.writeText(url);
    } catch (_e) {
      // Fallback for browsers without clipboard permission — silently
      // ignore, the UI still shows a "Copied!" pulse.
    }
    setCopiedPath(entry.path);
    window.setTimeout(() => setCopiedPath((cur) => (cur === entry.path ? null : cur)), 1600);
  }, [origin]);

  return (
    <div className="min-h-screen bg-[#FDFBF7] text-[#2C2621]">
      {/* Header — mirrors /admin/images so admins feel at home. */}
      <div className="border-b border-[#2C2621]/10 bg-[#F5EEDF]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-8 md:py-10">
          <div className="flex items-start justify-between gap-6 flex-wrap">
            <div>
              <Link
                to="/admin"
                className="inline-flex items-center gap-2 text-[10px] tracking-[0.28em] uppercase text-[#5C5248] hover:text-[#C16542] mb-4"
                data-testid="admin-routes-back"
              >
                <ArrowLeft className="w-3 h-3" strokeWidth={1.6} />
                Admin hub
              </Link>
              <h1 className="text-[10px] tracking-[0.32em] uppercase text-[#C16542] mb-2">
                Admin · Routes directory
              </h1>
              <h2 className="text-3xl md:text-4xl font-serif tracking-tight mb-2">
                Every URL in one place
              </h2>
              <p className="text-sm text-[#5C5248] max-w-2xl">
                Auto-generated from <code className="text-xs">src/lib/routesRegistry.jsx</code>.
                Adding or removing a route in that file updates this page and the app router
                simultaneously — no manual maintenance required.
              </p>
            </div>
            {/* Stats pills */}
            <div className="flex flex-wrap items-center gap-2" data-testid="admin-routes-stats">
              <div className="px-4 py-2.5 border border-[#2C2621]/15 bg-[#FDFBF7]">
                <p className="text-[10px] tracking-[0.22em] uppercase text-[#5C5248]">Total</p>
                <p className="text-lg font-medium">{stats.total}</p>
              </div>
              {Object.keys(CATEGORIES).map((catKey) => (
                <button
                  key={catKey}
                  type="button"
                  onClick={() => setCat(activeCat === catKey ? "all" : catKey)}
                  className={`px-4 py-2.5 border transition-all ${
                    activeCat === catKey
                      ? "border-[#C16542] bg-[#C16542]/10 ring-2 ring-offset-2 ring-[#C16542]/30"
                      : "border-[#2C2621]/15 bg-[#FDFBF7] hover:border-[#C16542]"
                  }`}
                  data-testid={`admin-routes-stat-${catKey}`}
                >
                  <p className="text-[10px] tracking-[0.22em] uppercase text-[#5C5248]">
                    {CATEGORIES[catKey].label}
                  </p>
                  <p className="text-lg font-medium">{stats[catKey] || 0}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Search + reset */}
          <div className="mt-6 flex items-center gap-3 flex-wrap">
            <div className="relative flex-1 min-w-[280px] max-w-lg">
              <Search className="w-4 h-4 text-[#5C5248] absolute left-3 top-1/2 -translate-y-1/2" strokeWidth={1.6} />
              <input
                type="text"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Filter by path, name or description…"
                className="w-full pl-10 pr-4 py-2.5 bg-[#FDFBF7] border border-[#2C2621]/20 focus:border-[#C16542] outline-none text-sm placeholder-[#5C5248]/60"
                data-testid="admin-routes-search"
              />
            </div>
            {(q || activeCat !== "all") && (
              <button
                type="button"
                onClick={() => { setQ(""); setCat("all"); }}
                className="text-[10px] tracking-[0.22em] uppercase text-[#5C5248] hover:text-[#C16542]"
                data-testid="admin-routes-clear"
              >
                Clear filters
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Routes list, grouped by category. */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-10 md:py-14">
        {filtered.length === 0 && (
          <p className="text-sm text-[#5C5248]" data-testid="admin-routes-empty">
            No routes match your filter.
          </p>
        )}
        {grouped.map((group) => (
          <section
            key={group.key}
            className="mb-14"
            data-testid={`admin-routes-group-${group.key}`}
          >
            <header className="flex items-baseline justify-between gap-4 mb-5 border-b border-[#2C2621]/10 pb-3">
              <div>
                <h3 className="text-[11px] tracking-[0.28em] uppercase text-[#C16542]">
                  {CATEGORIES[group.key].label}
                </h3>
                <p className="text-xs text-[#5C5248] mt-1">
                  {CATEGORIES[group.key].description}
                </p>
              </div>
              <p className="text-xs text-[#5C5248]">
                {group.routes.length} route{group.routes.length === 1 ? "" : "s"}
              </p>
            </header>
            <ul className="divide-y divide-[#2C2621]/10">
              {group.routes.map((entry) => {
                const params      = paramNames(entry.path);
                const previewPath = buildPreviewPath(entry);
                const openUrl     = origin + previewPath;
                const badgeStyle  = CATEGORY_BADGE_STYLES[entry.category] || DEFAULT_BADGE_STYLE;
                const copyId      = `admin-routes-copy-${entry.path.replace(/[^a-zA-Z0-9]+/g, "_")}`;
                const openId      = `admin-routes-open-${entry.path.replace(/[^a-zA-Z0-9]+/g, "_")}`;
                const isCopied    = copiedPath === entry.path;
                return (
                  <li
                    key={entry.path}
                    className="py-5 grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-6 items-start"
                    data-testid={`admin-routes-row-${entry.path.replace(/[^a-zA-Z0-9]+/g, "_")}`}
                  >
                    {/* Path + params */}
                    <div className="md:col-span-5 min-w-0">
                      <code className="block text-sm font-medium break-all text-[#2C2621]">
                        {entry.path}
                      </code>
                      {params.length > 0 && (
                        <p className="mt-1 text-[10px] tracking-[0.18em] uppercase text-[#5C5248]">
                          Params: {params.map((p) => (
                            <span key={p} className="inline-block bg-[#E8DBC4] text-[#2C2621] px-1.5 py-0.5 mr-1 rounded-sm font-mono text-[10px] normal-case tracking-normal">
                              :{p}
                            </span>
                          ))}
                        </p>
                      )}
                      {params.length > 0 && previewPath !== entry.path && (
                        <p className="mt-1 text-[10px] text-[#5C5248]/80 break-all">
                          Preview: <code className="text-[10px]">{previewPath}</code>
                        </p>
                      )}
                    </div>
                    {/* Name + description */}
                    <div className="md:col-span-5 min-w-0">
                      <p className="text-sm font-medium">{entry.name}</p>
                      {entry.description && (
                        <p className="mt-1 text-xs text-[#5C5248] leading-relaxed">
                          {entry.description}
                        </p>
                      )}
                      <div className="mt-2 flex items-center gap-2">
                        <span className={`inline-block text-[9px] tracking-[0.22em] uppercase px-2 py-1 ${badgeStyle}`}>
                          {CATEGORIES[entry.category]?.label || entry.category}
                        </span>
                        {/* Every route in the registry is by definition
                            mounted in App.js — reflect that explicitly
                            so admins never wonder if a link is broken. */}
                        <span className="inline-block text-[9px] tracking-[0.22em] uppercase px-2 py-1 bg-[#F5EEDF] text-[#3F6E4A] border border-[#3F6E4A]/30">
                          Active
                        </span>
                      </div>
                    </div>
                    {/* Actions */}
                    <div className="md:col-span-2 flex items-center gap-2 md:justify-end">
                      <a
                        href={openUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 border border-[#2C2621]/30 hover:border-[#C16542] hover:text-[#C16542] px-3 py-2 text-[10px] tracking-[0.24em] uppercase transition-colors"
                        data-testid={openId}
                        title={`Open ${openUrl}`}
                      >
                        <ExternalLink className="w-3 h-3" strokeWidth={1.8} />
                        Open
                      </a>
                      <button
                        type="button"
                        onClick={() => handleCopy(entry)}
                        className={`inline-flex items-center gap-1.5 px-3 py-2 text-[10px] tracking-[0.24em] uppercase transition-colors border ${
                          isCopied
                            ? "border-[#3F6E4A] bg-[#3F6E4A] text-[#FDFBF7]"
                            : "border-[#2C2621]/30 hover:border-[#C16542] hover:text-[#C16542]"
                        }`}
                        data-testid={copyId}
                        title={`Copy ${openUrl}`}
                      >
                        {isCopied ? (
                          <>
                            <Check className="w-3 h-3" strokeWidth={2} />
                            Copied
                          </>
                        ) : (
                          <>
                            <Copy className="w-3 h-3" strokeWidth={1.8} />
                            Copy
                          </>
                        )}
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          </section>
        ))}
      </div>
    </div>
  );
};

const AdminRoutesPage = () => (
  <AdminAuthGate>
    <AdminRoutesPageInner />
  </AdminAuthGate>
);

export default AdminRoutesPage;
